import type { V2 } from '../core/math';
import type { ResolvedSkill, SkillAction } from './skillTypes';
import { type BaseStats, type DerivedStats, defaultBaseStats, deriveStats, emptyMods } from './stats';
import type { PassiveRef } from './passives';
import type { StatusInstance } from './status';
import type { ActionKey, Element, EntityId, Grade, PeerId, SuperArmor, Team } from './types';

export type ActorKind = 'player' | 'monster' | 'npc' | 'summon';

/** An in-progress skill cast. */
export interface CastState {
  castId: number;
  skill: ResolvedSkill;
  key: ActionKey | null;
  stage: number;
  phase: 'casting' | 'stage' | 'holding' | 'charging';
  /** Time in the current phase (already scaled by attack speed). */
  t: number;
  aimAngle: number;
  aimPoint: V2;
  /** Index of the next timeline entry to fire in the current phase. */
  next: number;
  timeline: TimelineEntry[];
  /** Holding / charging bookkeeping. */
  holdTime: number;
  held: boolean;
  tickTimer: number;
  chargeLevel: number;
  perfect: boolean;
  /** Input for the next combo stage arrived during this stage. */
  queued: boolean;
  /** Stage duration after attack-speed scaling. */
  duration: number;
  cancelAt: number;
  /** Charge/perfect multiplier applied to hits fired by this stage. */
  mult: number;
  /** Radius multiplier for shapes fired by this stage (charge levels). */
  areaScale: number;
  /** Currently playing the skill's pre-stage. */
  pre: boolean;
}

export interface TimelineEntry {
  at: number;
  action: SkillAction;
  /** Multiplier on damage for split multi-hits (1/count). */
  split: number;
}

/** Active forced displacement (dash, knockback, pull). */
export interface Displacement {
  vx: number;
  vz: number;
  remaining: number;
  ignoreCollision: boolean;
  /** Dashes the actor performs on purpose (vs being pushed). */
  voluntary: boolean;
}

export interface StaggerState {
  max: number;
  value: number;
  /** False while recovering after a stagger or when not in a stagger check. */
  active: boolean;
  recoverTimer: number;
}

export interface PartState {
  id: string;
  name: string;
  max: number;
  value: number;
  broken: boolean;
}

export interface MonsterState {
  defId: string;
  grade: Grade;
  level: number;
  home: V2;
  leash: number;
  aggroRange: number;
  targetId: EntityId | null;
  /** Threat per attacker, decays over time. */
  threat: Map<EntityId, number>;
  stagger: StaggerState | null;
  parts: PartState[];
  /** World time until which the monster is counterable (blue glow). */
  counterUntil: number;
  /** Remaining helpless time after a successful counter. */
  countered: number;
  hpBars: number;
  weakness: Element | null;
  immunePush: boolean;
  immuneCC: boolean;
  lootTable: string | null;
  xp: number;
  /** Arbitrary per-brain state (boss scripts, pattern cursors). */
  brain: Record<string, unknown>;
  /** Seconds since spawn. */
  age: number;
  /** Seconds since it last dealt or took damage (for leash/regeneration). */
  idle: number;
  /** Elite chaos-rift bonus etc. */
  tags: string[];
}

export interface IdentityState {
  gauge: number;
  max: number;
  /** Class-specific state label ('idle', 'frenzy', 'fiend', stance names...). */
  mode: string;
  timer: number;
  stacks: number;
  counter: number;
  cooldown: number;
  /** Free-form extra fields for identity controllers. */
  data: Record<string, number>;
}

export interface PlayerState {
  classId: string;
  level: number;
  identity: IdentityState;
  /** Skill ids in slot order Q W E R A S D F. */
  bar: (string | null)[];
  awakening: string | null;
  dash: string;
  basic: string;
  skillLevels: Record<string, number>;
  /** Selected tripod ids per skill id (index = tier-1). */
  tripods: Record<string, (string | null)[]>;
  /** Cooldown for the stand-up (get up) action. */
  standUpCd: number;
  /** Awakening uses remaining in the current instance. */
  awakeningUses: number;
  /** Remaining time before a combo/chain follow-up expires. */
  followUp: { skillId: string; stage: number; until: number; key: ActionKey; stagesUsed: number } | null;
  /** Buffered input (pressed while locked). */
  buffered: { key: ActionKey; until: number; aim: V2 } | null;
  deaths: number;
  /** Stages used in the current combo/chain sequence (for chain cooldown penalties). */
  seqUsed: number;
  /** Per-skill modifiers from gems (applied after tripods). */
  skillMods: Record<string, { dmg?: number; cd?: number }>;
  /** PvP normalisation: skills cost no MP and stats are fixed. */
  pvpMode: boolean;
}

export interface ActorInit {
  id: EntityId;
  kind: ActorKind;
  owner: PeerId;
  team: Team;
  name: string;
  pos: V2;
  facing?: number;
  radius?: number;
  base?: Partial<BaseStats>;
}

export class Actor {
  readonly id: EntityId;
  kind: ActorKind;
  owner: PeerId;
  team: Team;
  name: string;

  pos: V2;
  /** Position at the previous tick, for render interpolation. */
  prevPos: V2;
  facing: number;
  radius: number;
  /** Visual height (airborne arcs). */
  height = 0;
  /** Current planar velocity (for animation blending). */
  speed = 0;

  base: BaseStats;
  stats: DerivedStats;
  hp: number;
  mp: number;
  shield = 0;
  alive = true;
  /** World time of death (for corpse cleanup / revive timers). */
  diedAt = 0;

  statuses: StatusInstance[] = [];
  /** Build passives (sigils, set bonuses, cards) with runtime hooks. */
  passives: PassiveRef[] = [];
  cooldowns = new Map<string, number>();
  /** Recharging charges for stackable skills: skillId -> charges available. */
  charges = new Map<string, number>();
  skills = new Map<string, ResolvedSkill>();

  cast: CastState | null = null;
  displacement: Displacement | null = null;
  /** Knocked down (lying on the ground). */
  downTimer = 0;
  /** Airborne (launched) timer and total. */
  airTimer = 0;
  airTotal = 0;
  /** Brief flinch (soft paralysis). */
  flinchTimer = 0;
  /** Invulnerable until this world time. */
  invulnUntil = 0;
  /** Super armor granted outside of casts (identity states etc.). */
  superArmorBonus: SuperArmor = 'none';

  moveGoal: V2 | null = null;
  path: V2[] = [];
  /** Continuous move direction (held mouse / gamepad), overrides path when set. */
  moveDir: V2 | null = null;

  /** Seconds since the actor last dealt or took damage. */
  combatIdle = 99;

  monster: MonsterState | null = null;
  player: PlayerState | null = null;
  /** NPC data id (dialogue, services). */
  npcId: string | null = null;

  /** Free-form render hints (model id, tint, scale). */
  look: { model: string; tint?: number; scale?: number; weapon?: string; variant?: string; pet?: string } = { model: 'hero' };

  /** Set when stats need recomputing (statuses/gear changed). */
  statsDirty = true;

  constructor(init: ActorInit) {
    this.id = init.id;
    this.kind = init.kind;
    this.owner = init.owner;
    this.team = init.team;
    this.name = init.name;
    this.pos = { ...init.pos };
    this.prevPos = { ...init.pos };
    this.facing = init.facing ?? 0;
    this.radius = init.radius ?? 0.5;
    this.base = { ...defaultBaseStats(), ...init.base };
    this.stats = deriveStats(this.base, emptyMods());
    this.hp = this.base.maxHp;
    this.mp = this.base.maxMp;
  }

  get isPlayer(): boolean {
    return this.kind === 'player';
  }

  get hpPct(): number {
    return this.stats.maxHp > 0 ? this.hp / this.stats.maxHp : 0;
  }

  hasStatus(id: string): boolean {
    return this.statuses.some((s) => s.id === id);
  }

  getStatus(id: string): StatusInstance | undefined {
    return this.statuses.find((s) => s.id === id);
  }

  setBase(base: BaseStats): void {
    const hpFrac = this.hpPct;
    const mpFrac = this.stats.maxMp > 0 ? this.mp / this.stats.maxMp : 1;
    this.base = base;
    this.statsDirty = true;
    this.stats = deriveStats(this.base, this.stats.mods);
    this.hp = Math.max(1, Math.round(this.stats.maxHp * hpFrac));
    this.mp = this.stats.maxMp * mpFrac;
  }
}
